import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, InputAdornment, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useLocation, useParams } from 'react-router-dom';
import api from '../../api/appPublicApi';
import { maxShelfTagCopies, shelfTagOptions, shelfTagSizes } from './shelfTags.mjs';
import { openShelfTagPdf } from './shelfTagPdf.mjs';
import { readShelfTagRequest, shelfTagProduct } from './shelfTagEmbed.mjs';

export default function ItemShelfTags() {
  const { itemNumber } = useParams();
  const { search } = useLocation();
  const [fields, setFields] = useState(() => readShelfTagRequest(search));
  const [request, setRequest] = useState({ loading: true });
  const [submitted, setSubmitted] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const generatingRef = useRef(false);

  useEffect(() => {
    setFields(readShelfTagRequest(search));
    setSubmitted(false);
    setError('');
  }, [itemNumber, search]);

  useEffect(() => {
    const controller = new AbortController();
    if (!/^\d{1,30}$/.test(itemNumber || '')) {
      setRequest({ itemNumber, error: 'Enter a valid item number in the URL.' });
      return undefined;
    }
    setRequest({ itemNumber, loading: true });
    api.get(`/api/app/itemSpecSheet/${encodeURIComponent(itemNumber)}`, { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        const product = shelfTagProduct(data, itemNumber);
        setRequest(product ? { itemNumber, product } : { itemNumber, error: `Invalid Product Number: ${itemNumber}` });
      })
      .catch(() => {
        if (!controller.signal.aborted) setRequest({ itemNumber, error: 'Unable to load this item. Reload the page to try again.' });
      });
    return () => controller.abort();
  }, [itemNumber]);

  const loading = request.loading || request.itemNumber !== itemNumber;
  const product = !loading && request.product;
  const options = useMemo(() => ({ product, ...fields }), [product, fields]);
  const result = useMemo(() => shelfTagOptions(options), [options]);
  const errors = submitted ? result.errors : {};
  const update = key => event => {
    setFields(previous => ({ ...previous, [key]: event.target.value }));
    setError('');
  };
  const generate = async event => {
    event.preventDefault();
    setSubmitted(true);
    setError('');
    if (!product || generatingRef.current || Object.keys(result.errors).length) return;
    generatingRef.current = true;
    setGenerating(true);
    try { await openShelfTagPdf(options); }
    catch (failure) { setError(failure.message || 'Labels could not be generated. Please try again.'); }
    finally { generatingRef.current = false; setGenerating(false); }
  };

  return <Box sx={{ minHeight: '100vh', bgcolor: '#f3f5f8', p: { xs: 1.5, sm: 3 }, boxSizing: 'border-box' }}>
    <Paper variant='outlined' sx={{ maxWidth: 580, mx: 'auto', p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
        <LocalOfferOutlinedIcon color='primary' />
        <Typography component='h1' variant='h6' sx={{ fontWeight: 700 }}>Create Shelf Tags</Typography>
      </Stack>
      {loading ? <Stack direction='row' alignItems='center' spacing={2} role='status'><CircularProgress size={24} /><Typography>Loading item data…</Typography></Stack>
        : request.error ? <Alert severity='error'>{request.error}</Alert>
          : product && <>
            <Typography variant='body2' sx={{ overflowWrap: 'anywhere' }}>{product.name}</Typography>
            <Typography variant='caption' color='text.secondary'>Item {product.itemNumber}</Typography>
            <Box component='form' noValidate onSubmit={generate} sx={{ mt: 3 }}>
              <Stack spacing={2.5}>
                <TextField select fullWidth size='small' label='Label size' value={fields.sizeId} onChange={update('sizeId')}
                  disabled={generating} error={Boolean(errors.size)} helperText={errors.size || 'Width × height, in inches'}>
                  {!shelfTagSizes.some(size => size.id === fields.sizeId) && <MenuItem value={fields.sizeId}>Unsupported size: {fields.sizeId || '(empty)'}</MenuItem>}
                  {shelfTagSizes.map(size => <MenuItem key={size.id} value={size.id}>{size.label}</MenuItem>)}
                </TextField>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1.4fr) minmax(0, 1fr)' }, gap: 2.5 }}>
                  <TextField fullWidth required size='small' label='Price' value={fields.price} onChange={update('price')}
                    disabled={generating} error={Boolean(errors.price)}
                    helperText={errors.price || 'The price to print on each label.'}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }} inputProps={{ inputMode: 'decimal', maxLength: 12 }} />
                  <TextField fullWidth size='small' label='Number of labels' type='number' value={fields.copies} onChange={update('copies')}
                    disabled={generating} error={Boolean(errors.copies)} helperText={errors.copies || `1–${maxShelfTagCopies} labels`}
                    inputProps={{ min: 1, max: maxShelfTagCopies, step: 1, inputMode: 'numeric' }} />
                </Box>
                {(error || result.errors.barcode) && <Alert severity='error'>{error || result.errors.barcode}</Alert>}
                <Button type='submit' variant='contained' disableElevation endIcon={<OpenInNewRoundedIcon />} disabled={generating || Boolean(result.errors.barcode)} sx={{ minHeight: 44 }}>
                  {generating ? 'Generating…' : 'Generate Labels'}
                </Button>
                <Typography variant='caption' color='text.secondary' sx={{ textAlign: 'center' }}>
                  Opens in a new tab. One label per page. Print at actual size (100%).
                </Typography>
              </Stack>
            </Box>
          </>}
    </Paper>
  </Box>;
}
